import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import type { Database } from '@/lib/database.types';

type Order = Database['public']['Tables']['orders']['Row'];

export interface RecentOrder extends Order {
  table?: { name: string } | null;
  customer?: { first_name: string | null; last_name: string | null } | null;
  server?: { first_name: string | null; last_name: string | null } | null;
  payment?: { payment_method: string } | null;
}

interface RecentActivityOptions {
  filterByUserId?: string;
  showAllLocations?: boolean;
}

export function useRecentActivity(limit: number = 10, options?: RecentActivityOptions) {
  const queryClient = useQueryClient();
  const { currentLocation, locations } = useLocationStore();
  const locationId = currentLocation?.id;
  const filterByUserId = options?.filterByUserId;
  const showAllLocations = options?.showAllLocations ?? false;
  
  // Get all location IDs for "all locations" mode
  const allLocationIds = locations.map(loc => loc.id);

  const {
    data: recentOrders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['recent-activity', showAllLocations ? 'all' : locationId, limit, filterByUserId, allLocationIds.join(',')],
    queryFn: async () => {
      // Need either a location or all-locations mode with locations
      if (!showAllLocations && !locationId) return [];
      if (showAllLocations && allLocationIds.length === 0) return [];

      let query = supabase
        .from('orders')
        .select(`
          *,
          table:tables(name),
          customer:customers(first_name, last_name),
          server:profiles!orders_user_id_fkey(first_name, last_name),
          payment:payments!payments_order_id_fkey(payment_method)
        `);

      // Filter by location or all org locations
      if (showAllLocations) {
        query = query.in('location_id', allLocationIds);
      } else {
        query = query.eq('location_id', locationId);
      }

      // Filter by server/cashier if specified (user_id column)
      if (filterByUserId) {
        query = query.eq('user_id', filterByUserId);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      
      // Transform data to flatten the payment array to single payment
      return (data as any[]).map(order => ({
        ...order,
        payment: order.payment?.[0] || null,
      })) as RecentOrder[];
    },
    enabled: showAllLocations ? allLocationIds.length > 0 : !!locationId,
    refetchInterval: 20000, // Backup poll; realtime subscription handles live updates
  });

  // Subscribe to realtime updates
  useEffect(() => {
    // For all-locations mode, we subscribe without location filter
    // For single location mode, we filter by location
    if (!showAllLocations && !locationId) return;
    if (showAllLocations && allLocationIds.length === 0) return;

    const channelName = showAllLocations 
      ? `recent-activity:all-locations` 
      : `recent-activity:${locationId}`;

    // Note: Supabase realtime filtering is limited, so for all-locations mode
    // we subscribe to all order changes and let the query refetch
    const channelConfig = showAllLocations
      ? {
          event: '*' as const,
          schema: 'public',
          table: 'orders',
        }
      : {
          event: '*' as const,
          schema: 'public',
          table: 'orders',
          filter: `location_id=eq.${locationId}`,
        };

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', channelConfig, () => {
        queryClient.invalidateQueries({ 
          queryKey: ['recent-activity', showAllLocations ? 'all' : locationId, limit, filterByUserId, allLocationIds.join(',')] 
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [locationId, limit, filterByUserId, queryClient, showAllLocations, allLocationIds]);

  // Helper to get time ago string
  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Helper to get status display info
  const getStatusInfo = (status: Order['status']): { label: string; variant: 'success' | 'warning' | 'default' | 'destructive' } => {
    switch (status) {
      case 'completed':
        return { label: 'Paid', variant: 'success' };
      case 'sent':
      case 'in_progress':
        return { label: 'In Kitchen', variant: 'warning' };
      case 'ready':
        return { label: 'Ready', variant: 'default' };
      case 'open':
        return { label: 'Open', variant: 'default' };
      case 'cancelled':
        return { label: 'Cancelled', variant: 'destructive' };
      case 'refunded':
        return { label: 'Refunded', variant: 'destructive' };
      default:
        return { label: status ?? 'Unknown', variant: 'default' };
    }
  };

  return {
    recentOrders: recentOrders ?? [],
    isLoading,
    error,
    refetch,
    getTimeAgo,
    getStatusInfo,
  };
}





